import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

export interface AppNotification {
  id: string;
  title: string;
  message: string;
  type: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR' | 'CAMPAIGN' | 'BACKUP' | 'SYSTEM';
  read: boolean;
  readAt: string | null;
  createdAt: string;
  data?: Record<string, unknown>;
  /** Tipo do alerta de origem (SESSION_FAILED, QUOTA_WARNING…), quando veio de um alerta */
  alertType?: string;
}

export interface UseNotificationsReturn {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  connected: boolean;
  markAsRead: (id: string) => void;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  refresh: () => void;
}

function mapAlertType(type: string): AppNotification['type'] {
  const map: Record<string, AppNotification['type']> = {
    QUOTA_WARNING: 'WARNING',
    QUOTA_CRITICAL: 'ERROR',
    SESSION_FAILED: 'ERROR',
    CAMPAIGN_FAILED: 'ERROR',
    CAMPAIGN_COMPLETED: 'SUCCESS',
    BACKUP_FAILED: 'ERROR',
    BACKUP_SUCCESS: 'BACKUP',
    SYSTEM_ERROR: 'ERROR',
    SYSTEM_INFO: 'INFO',
    WARNING: 'WARNING',
    ERROR: 'ERROR',
    INFO: 'INFO',
    SUCCESS: 'SUCCESS',
  };
  return map[type] || 'INFO';
}

export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  const authenticatedFetch = useCallback(async (url: string, options?: RequestInit) => {
    const token = localStorage.getItem('auth_token');
    return fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...((options?.headers as Record<string, string>) || {}),
      },
    });
  }, []);

  const loadViaRest = useCallback(async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        authenticatedFetch('/api/notifications?limit=50'),
        authenticatedFetch('/api/notifications/unread-count'),
      ]);
      if (notifRes.ok) {
        const data = await notifRes.json();
        const mapped: AppNotification[] = (data.notifications || []).map((n: Record<string, any>) => ({
          id: n.id,
          title: n.alert?.title || 'Notificação',
          message: n.alert?.message || '',
          type: mapAlertType(n.alert?.type || n.alert?.severity || 'INFO'),
          read: n.read,
          readAt: n.readAt,
          createdAt: n.createdAt,
          data: n.alert?.metadata || undefined,
          alertType: n.alert?.type,
        }));
        setNotifications(mapped);
      }
      if (countRes.ok) {
        const data = await countRes.json();
        setUnreadCount(data.unreadCount || 0);
      }
    } catch {
      // silencioso — sem notificações disponíveis
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const refresh = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('get_notifications', { page: 1, limit: 50 });
    } else {
      loadViaRest();
    }
  }, [loadViaRest]);

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    if (socketRef.current?.connected) {
      socketRef.current.emit('mark_notification_read', id);
    } else {
      authenticatedFetch(`/api/notifications/${id}/mark-read`, { method: 'POST' });
    }
  }, [authenticatedFetch]);

  const markAllAsRead = useCallback(async () => {
    try {
      await authenticatedFetch('/api/notifications/mark-all-read', { method: 'POST' });
      setNotifications(prev => prev.map(n => ({ ...n, read: true, readAt: new Date().toISOString() })));
      setUnreadCount(0);
    } catch {
      // silencioso
    }
  }, [authenticatedFetch]);

  const deleteNotification = useCallback(async (id: string) => {
    const existing = notifications.find(n => n.id === id);
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (existing && !existing.read) setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await authenticatedFetch(`/api/notifications/${id}`, { method: 'DELETE' });
    } catch {
      // Reverte em caso de erro
      if (existing) {
        setNotifications(prev => [existing, ...prev].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ));
        if (!existing.read) setUnreadCount(prev => prev + 1);
      }
    }
  }, [notifications, authenticatedFetch]);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setLoading(false);
      return;
    }

    const socket = io('/', {
      path: '/socket.io/',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('get_notifications', { page: 1, limit: 50 });
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('connect_error', () => {
      loadViaRest();
    });

    socket.on('notifications_data', (data: { notifications: AppNotification[] }) => {
      setNotifications(data.notifications);
      setUnreadCount(data.notifications.filter(n => !n.read).length);
      setLoading(false);
    });

    socket.on('new_notification', (notification: AppNotification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    });

    socket.on('unread_notifications_count', (data: { count: number }) => {
      setUnreadCount(data.count);
    });

    socket.on('notification_marked_read', (data: { notificationId: string }) => {
      setNotifications(prev =>
        prev.map(n => n.id === data.notificationId ? { ...n, read: true, readAt: new Date().toISOString() } : n)
      );
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [loadViaRest]);

  return { notifications, unreadCount, loading, connected, markAsRead, markAllAsRead, deleteNotification, refresh };
}
